import type { ChainFamily } from 'src/blockchain';
import type { ChallengePurpose, ApiAuthChallenge, ApiSignedChallenge } from 'src/types/wallet';

import { useCallback } from 'react';

import axios from 'src/utils/axios';
import { buildSignedChallenge, buildChallengeRequest } from 'src/utils/walletChallenge';

import { useWallet, useWalletUI } from 'src/blockchain';

/** Thrown before anything is sent, when there is no wallet to sign with. */
export class WalletNotConnectedError extends Error {
  constructor(message = 'Connect the wallet linked to your account to continue.') {
    super(message);
    this.name = 'WalletNotConnectedError';
  }
}

/**
 * Proof of wallet ownership for a single action: ask api for a challenge, sign it in the wallet,
 * hand the signed payload to the caller.
 *
 * This is the rail wallet login has always used, with one difference that matters: since api#131 a
 * challenge is **one-time and bound to a purpose**. So every call fetches a fresh challenge instead
 * of reading a cached one — a challenge is burnt the moment it reaches the server, which made the
 * second attempt after any failure (a rejected signature, a wrong code) fail as well. The purpose
 * travels with the request too: a challenge issued for login is refused by an email change, and
 * vice versa.
 *
 * Used both as the second factor of an email change (`email_change`) and by the plain wallet flows
 * that used to read the cached challenge — logging in and linking a wallet (`auth`).
 *
 * The family is required, like `WalletButton`'s (app#243): the challenge is issued and verified
 * per family, and the default family used to sign for screens that never said which wallet they
 * meant — TRON, for a buyer holding only MetaMask.
 */
export function useWalletSecondFactor(family: ChainFamily) {
  const { wallet, isConnected } = useWallet(family);
  const { signMessage } = useWalletUI(family);

  const address = wallet?.address;
  const publicKey = wallet?.publicKey;

  const sign = useCallback(
    async (purpose: ChallengePurpose): Promise<ApiSignedChallenge> => {
      if (!isConnected || !address) {
        throw new WalletNotConnectedError();
      }

      const { data: challenge } = await axios.post<ApiAuthChallenge>(
        `/wallet/auth/challenge/get`,
        buildChallengeRequest({ walletAddress: address, purpose, family })
      );

      const signed = await signMessage(challenge.challenge);

      return buildSignedChallenge({ family, address, publicKey, signed });
    },
    [address, family, isConnected, publicKey, signMessage]
  );

  // One readiness flag for callers to gate their button on, and it is exactly the condition `sign`
  // checks. An adapter can report itself connected while the address is momentarily empty (account
  // switch, extension re-opened); gating on `isConnected` alone would offer a button whose only
  // outcome is "connect your wallet" as an error.
  return { sign, canSign: isConnected && !!address, address };
}
